/**
 * Roterende logomuur (de certificaten in de footer).
 *
 * Er zijn meer certificaten dan er plekken in beeld zijn. De overtollige plekken
 * worden in CSS op display:none gezet -- dit script leest dat uit om te bepalen
 * hoeveel plekken er meedoen, zodat het aantal per breakpoint kan verschillen
 * zonder dat de JS iets van breakpoints hoeft te weten.
 *
 * Elke tik wisselt er één plek: het oude logo schuift omhoog weg, het nieuwe komt
 * van onderen terug. De volgorde waarin de plekken aan de beurt komen is per
 * pagina-load door elkaar gehusseld, zodat het niet als een golfje van links naar
 * rechts leest.
 *
 * Gebaseerd op de Osmo Supply "Logo Wall Cycle" -- de data-attributen komen
 * daarvandaan en zijn nodig voor het uitlezen van de DOM.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const LOOP_DELAY = 1.5;
const DURATION = 0.9;

export default function initLogoWall() {
  const roots = document.querySelectorAll('[data-logo-wall-cycle-init]');

  if (! roots.length) {
    return;
  }

  // Zonder beweging blijft de eerste rij gewoon staan: die is op zichzelf al een
  // complete weergave, alleen niet compleet qua aantal certificaten.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  roots.forEach((root) => {
    const list = root.querySelector('[data-logo-wall-list]');

    if (! list) {
      return;
    }

    const items = Array.from(list.querySelectorAll('[data-logo-wall-item]'));
    const shuffleFront = root.getAttribute('data-logo-wall-shuffle') !== 'false';
    const originalTargets = items
      .map((item) => item.querySelector('[data-logo-wall-target]'))
      .filter(Boolean);

    // Minder logo's dan plekken betekent dat er niets te wisselen valt.
    if (originalTargets.length <= items.filter(isVisible).length) {
      return;
    }

    let visibleItems = [];
    let visibleCount = 0;
    let pool = [];
    let pattern = [];
    let patternIndex = 0;
    let tl;

    function isVisible(el) {
      return window.getComputedStyle(el).display !== 'none';
    }

    function shuffleArray(arr) {
      const a = arr.slice();

      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }

      return a;
    }

    function setup() {
      if (tl) {
        tl.kill();
      }

      visibleItems = items.filter(isVisible);
      visibleCount = visibleItems.length;

      pattern = shuffleArray(Array.from({ length: visibleCount }, (_, i) => i));
      patternIndex = 0;

      items.forEach((item) => {
        item.querySelectorAll('[data-logo-wall-target]').forEach((old) => old.remove());
      });

      pool = originalTargets.map((node) => node.cloneNode(true));

      let front;
      let rest;

      if (shuffleFront) {
        const shuffledAll = shuffleArray(pool);
        front = shuffledAll.slice(0, visibleCount);
        rest = shuffleArray(shuffledAll.slice(visibleCount));
      } else {
        front = pool.slice(0, visibleCount);
        rest = shuffleArray(pool.slice(visibleCount));
      }

      pool = front.concat(rest);

      for (let i = 0; i < visibleCount; i++) {
        const parent = visibleItems[i].querySelector('[data-logo-wall-target-parent]') || visibleItems[i];
        parent.appendChild(pool.shift());
      }

      tl = gsap.timeline({ repeat: -1, repeatDelay: LOOP_DELAY });
      tl.call(swapNext);
      tl.play();
    }

    function swapNext() {
      // Bij een resize kan het aantal zichtbare plekken veranderd zijn; dan
      // opnieuw opbouwen in plaats van in een verkeerde index grijpen.
      const nowCount = items.filter(isVisible).length;

      if (nowCount !== visibleCount) {
        setup();

        return;
      }

      if (! pool.length) {
        return;
      }

      const idx = pattern[patternIndex % visibleCount];
      patternIndex++;

      const container = visibleItems[idx];
      const parent = container.querySelector('[data-logo-wall-target-parent]') || container;
      const existing = parent.querySelectorAll('[data-logo-wall-target]');

      // Deze plek is nog midden in een wissel: overslaan, anders stapelen ze op.
      if (existing.length > 1) {
        return;
      }

      const current = parent.querySelector('[data-logo-wall-target]');
      const incoming = pool.shift();

      gsap.set(incoming, { yPercent: 50, autoAlpha: 0 });
      parent.appendChild(incoming);

      if (current) {
        gsap.to(current, {
          yPercent: -50,
          autoAlpha: 0,
          duration: DURATION,
          ease: 'expo.inOut',
          onComplete: () => {
            current.remove();
            pool.push(current);
          },
        });
      }

      gsap.to(incoming, {
        yPercent: 0,
        autoAlpha: 1,
        duration: DURATION,
        delay: 0.1,
        ease: 'expo.inOut',
      });
    }

    setup();

    // Niet doordraaien terwijl niemand kijkt.
    ScrollTrigger.create({
      trigger: root,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => tl.play(),
      onLeave: () => tl.pause(),
      onEnterBack: () => tl.play(),
      onLeaveBack: () => tl.pause(),
    });

    document.addEventListener('visibilitychange', () => (document.hidden ? tl.pause() : tl.play()));
  });
}
